import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { AdminPermission } from '@/types/admin';

// ─── Nav structure ────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: AdminPermission;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

function Icon({ d, viewBox = '0 0 24 24' }: { d: string | string[]; viewBox?: string }) {
  const paths = Array.isArray(d) ? d : [d];
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox={viewBox} stroke="currentColor" strokeWidth={1.75}>
      {paths.map((p, i) => <path key={i} strokeLinecap="round" strokeLinejoin="round" d={p} />)}
    </svg>
  );
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    items: [
      {
        label: 'Dashboard',
        path: '/',
        icon: <Icon d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />,
      },
    ],
  },
  {
    title: 'Users',
    items: [
      {
        label: 'User Management',
        path: '/users',
        icon: <Icon d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
        permission: 'users.view',
      },
    ],
  },
  {
    title: 'Content',
    items: [
      {
        label: 'Classrooms',
        path: '/classrooms',
        icon: <Icon d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />,
        permission: 'classrooms.view',
      },
      {
        label: 'Content Moderation',
        path: '/content',
        icon: <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
        permission: 'content.view',
      },
    ],
  },
  {
    title: 'Platform',
    items: [
      {
        label: 'AI Tutor Oversight',
        path: '/ai',
        icon: <Icon d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H4a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-1" />,
        permission: 'ai.view',
      },
      {
        label: 'Translations',
        path: '/translations',
        icon: <Icon d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />,
        permission: 'translations.view',
      },
      {
        label: 'Notifications',
        path: '/notifications',
        icon: <Icon d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />,
        permission: 'notifications.view',
      },
    ],
  },
  {
    title: 'Monetisation',
    items: [
      {
        label: 'Billing',
        path: '/billing',
        icon: <Icon d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />,
        permission: 'billing.view',
      },
    ],
  },
  {
    title: 'Analytics',
    items: [
      {
        label: 'Analytics',
        path: '/analytics',
        icon: <Icon d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
        permission: 'analytics.view',
      },
    ],
  },
  {
    title: 'System',
    items: [
      {
        label: 'System Config',
        path: '/system',
        icon: <Icon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />,
        permission: 'system.monitoring',
      },
      {
        label: 'Impersonation & Debug',
        path: '/impersonation',
        icon: <Icon d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />,
        permission: 'users.impersonate',
      },
    ],
  },
  {
    title: 'Admin',
    items: [
      {
        label: 'Admin Accounts',
        path: '/admin-accounts',
        icon: <Icon d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
        permission: 'admins.view',
      },
    ],
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface SidebarProps {
  permissions: AdminPermission[];
  collapsed: boolean;
  onToggle: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Sidebar({ permissions, collapsed, onToggle }: SidebarProps) {
  const location = useLocation();

  function hasPermission(permission?: AdminPermission): boolean {
    if (!permission) return true;
    return permissions.includes(permission);
  }

  return (
    <aside
      className={cn(
        'flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-200 ease-in-out flex-shrink-0',
        collapsed ? 'w-14' : 'w-56',
      )}
    >
      {/* Logo / brand */}
      <div className="flex items-center h-14 px-3 border-b border-sidebar-border flex-shrink-0">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 w-full min-w-0 text-left hover:opacity-80 transition-opacity"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">T</span>
          </div>
          {!collapsed && (
            <span className="text-sm font-semibold text-sidebar-foreground truncate">TutorSnap</span>
          )}
        </button>
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-4">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter((item) => hasPermission(item.permission));
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title}>
              {!collapsed && (
                <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40 select-none">
                  {section.title}
                </p>
              )}
              <ul className="space-y-0.5 px-1.5">
                {visibleItems.map((item) => {
                  const isActive =
                    item.path === '/'
                      ? location.pathname === '/'
                      : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        title={collapsed ? item.label : undefined}
                        className={cn(
                          'flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors',
                          isActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                          collapsed && 'justify-center px-0',
                        )}
                      >
                        {item.icon}
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
